/**
 * AdminApplicationDetailPage — one gated-role application, reviewed.
 *
 * Reads GET /api/admin/applications/:id (AdminDashboardService
 * .getApplicationDetail). That payload is mixed-case by design: the users row
 * and `profile` come back raw (snake_case), while documents and the checklists
 * are mapped (camelCase) — the field names below follow it exactly.
 *
 * Content adapts to the applicant's role: contractors get business/trade and
 * licensing sections, verifiers get their verification scope. Read-only —
 * approve/reject still live in the legacy console.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  Alert,
  Skeleton,
  Link,
  Divider,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  RadioButtonUnchecked,
  OpenInNew,
  Person,
  Done,
  Close as CloseIcon,
} from '@mui/icons-material';

import adminApplicationsService from '../services/adminApplicationsService';
import RoleChip from '../components/admin/RoleChip';
import ApplicationActions from '../components/admin/ApplicationActions';
import { panelSx, heroPanelSx, panelHeaderSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, bg } from '../theme/colors';

const EMPTY = '—';

const APPROVAL_TONES = {
  approved: { fg: statusTokens.successDark, bg: statusTokens.successBg },
  pending: { fg: statusTokens.warningDark, bg: statusTokens.warningBg },
  rejected: { fg: statusTokens.errorDark, bg: statusTokens.errorBg },
};

const VERIFIER_TYPE_LABELS = { client: 'Work', contractor: 'Contractor' };

const VERIFIER_ROLES = ['verifier', 'professional_verifier', 'quality_inspector'];

const titleise = (value) =>
  value == null || value === '' ? EMPTY : String(value).replace(/_/g, ' ');

const formatDate = (value, withTime = false) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const Field = ({ label, value, span = 6 }) => (
  <Grid item xs={12} sm={span}>
    <Typography
      variant="caption"
      sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: text.primary, fontSize: 14, mt: 0.25, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Grid>
);

const Section = ({ title, action, children, grid = true }) => (
  <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
    <Box
      sx={{
        ...panelHeaderSx,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>{title}</Typography>
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>
      {grid ? (
        <Grid container spacing={2.5}>
          {children}
        </Grid>
      ) : (
        children
      )}
    </Box>
  </Paper>
);

const hasAny = (...values) =>
  values.some((v) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0));

const AdminApplicationDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const listSearch = location.state?.listSearch || '';
  const listPath = location.state?.listPath || '/admin/home/applications';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApplicationsService.detail(userId);
      setData(res?.data || null);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'That application does not exist.'
          : err?.response?.data?.error || err?.message || 'Could not load this application.'
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Row-level mutations: track which row is in flight so only that control spins.
  const [rowBusy, setRowBusy] = useState(null);
  const [rowError, setRowError] = useState(null);
  // Realtor: manual EAC verification notes (shown inline when the admin opts to override).
  const [eacEditing, setEacEditing] = useState(false);
  const [eacNotes, setEacNotes] = useState('');

  const runRow = async (key, fn) => {
    setRowBusy(key);
    setRowError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setRowError(
        err?.response?.data?.error || err?.message || 'That change could not be saved.'
      );
    } finally {
      setRowBusy(null);
    }
  };

  const toggleRequirement = (item) =>
    runRow(`req:${item.key}`, () =>
      adminApplicationsService.waiveRequirement(
        userId,
        item.key,
        !item.waived,
        !item.waived ? 'Waived by admin' : null
      )
    );

  const decideDocument = (doc, decision) =>
    runRow(`doc:${doc.id}`, () =>
      decision === 'approved'
        ? adminApplicationsService.approveDocument(doc.id)
        : adminApplicationsService.rejectDocument(doc.id)
    );

  // `profile` is the raw user_profiles row, so snake_case throughout.
  const profile = data?.profile;
  const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
  const isVerifier = VERIFIER_ROLES.includes(data?.role);
  const isContractor = data?.role === 'contractor';
  const isRealtor = data?.role === 'realtor';
  const eac = data?.eacStatus;
  const realtorProfile = data?.realtorProfile;
  const eacSeverity =
    eac?.state === 'approved' ? 'success' : eac?.state === 'rejected' ? 'error' : 'warning';
  const eacSummary = !eac
    ? EMPTY
    : eac.manualOverride
      ? `Verified manually — ${eac.manualOverride.notes || 'no notes'}`
      : eac.agency
        ? `${eac.agency.name} · ${eac.agency.status || 'unknown'}${eac.agency.disappeared_at ? ' · no longer listed' : ''}`
        : eac.registerAvailable
          ? `No match${eac.agencyNameClaimed ? ` for "${eac.agencyNameClaimed}"` : ''} — verify manually`
          : 'Register unavailable — verify manually';
  // Realtor: Eqaya's check with the agency (owner, 2026-09-27).
  const [agcEditing, setAgcEditing] = useState(false);
  const [agcNotes, setAgcNotes] = useState('');
  const setAgencyConfirmation = (confirmed, notes) =>
    runRow('agc', async () => {
      await adminApplicationsService.setRealtorAgencyConfirmation(userId, confirmed, notes || null);
      setAgcEditing(false);
      setAgcNotes('');
    });
  const setEacOverride = (verified, notes) =>
    runRow('eac', async () => {
      await adminApplicationsService.setRealtorEacOverride(userId, verified, notes || null);
      setEacEditing(false);
      setEacNotes('');
    });
  // Conveyancer: the Law Society register line, the same shape as the EAC one.
  const isConveyancer = data?.role === 'conveyancer';
  const lsz = data?.lszStatus;
  const conveyancerProfile = data?.conveyancerProfile;
  const lszSeverity =
    lsz?.state === 'approved' ? 'success' : lsz?.state === 'rejected' ? 'error' : 'warning';
  const lszEntry = lsz?.practitioner;
  const lszSummary = !lsz
    ? EMPTY
    : lsz.manualOverride
      ? `Verified manually — ${lsz.manualOverride.notes || 'no notes'}`
      : lszEntry
        ? `${lszEntry.surname}${lszEntry.first_names ? `, ${lszEntry.first_names}` : ''}${lszEntry.firm ? ` · ${lszEntry.firm}` : ''}${lszEntry.disappeared_at ? ' · no longer in the directory' : lszEntry.is_conveyancer ? ' · conveyancer' : ' · not a conveyancer'}`
        : lsz.registerAvailable
          ? 'Not linked to a directory entry yet'
          : 'Register unavailable — verify manually';
  const setLszOverride = (verified, notes) =>
    runRow('lsz', async () => {
      await adminApplicationsService.setConveyancerLszOverride(userId, verified, notes || null);
      setEacEditing(false);
      setEacNotes('');
    });
  const approvalTone = APPROVAL_TONES[data?.approval_status] || {
    fg: statusTokens.neutral,
    bg: statusTokens.neutralBg,
  };

  const checklist = data?.applicationChecklist;
  const documents = data?.documents || [];

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`${listPath}${listSearch}`)}
        sx={{ textTransform: 'none', color: text.secondary, mb: 2 }}
      >
        Back to applications
      </Button>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2, mb: 2.5 }} />
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
        </>
      ) : !data ? null : (
        <>
          {/* Identity */}
          <Paper
            elevation={0}
            sx={{
              ...heroPanelSx,
              p: 2.5,
              mb: 2.5,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2.5,
            }}
          >
            <Avatar
              src={profile?.profile_image_url || undefined}
              alt={name || data.email}
              sx={{
                width: 72,
                height: 72,
                fontSize: 26,
                fontWeight: 600,
                bgcolor: `${brand.primary}1a`,
                color: brand.primary,
              }}
            >
              {(name || data.email || '?').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
                {name || data.email}
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary, mb: 1.25 }}>
                {data.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <RoleChip role={data.role} />
                <Chip
                  size="small"
                  label={titleise(data.approval_status)}
                  sx={{
                    textTransform: 'capitalize',
                    fontWeight: 600,
                    fontSize: 12,
                    color: approvalTone.fg,
                    bgcolor: approvalTone.bg,
                  }}
                />
                <Typography variant="caption" sx={{ color: text.tertiary, ml: 0.5 }}>
                  Applied {formatDate(data.created_at)}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Person />}
              onClick={() => navigate(`/admin/home/users/${data.id}`)}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              User record
            </Button>
          </Paper>

          {data.approval_status === 'rejected' && data.approval_notes && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              <strong>Rejection reason:</strong> {data.approval_notes}
            </Alert>
          )}

          {/* Admin actions */}
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: text.tertiary,
                fontWeight: 600,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Admin actions
            </Typography>
            <ApplicationActions application={data} onChanged={load} />
          </Paper>

          {rowError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setRowError(null)}>
              {rowError}
            </Alert>
          )}

          {/* Requirements */}
          {checklist?.items?.length ? (
            <Section
              title="Application requirements"
              grid={false}
              action={
                <Chip
                  size="small"
                  label={checklist.complete ? 'Complete' : `${checklist.missing.length} outstanding`}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: checklist.complete ? statusTokens.successDark : statusTokens.warningDark,
                    bgcolor: checklist.complete ? statusTokens.successBg : statusTokens.warningBg,
                  }}
                />
              }
            >
              <Grid container spacing={1.25}>
                {checklist.items.map((item) => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.satisfied ? (
                        <CheckCircle sx={{ fontSize: 18, color: statusTokens.success }} />
                      ) : item.optional ? (
                        <RadioButtonUnchecked sx={{ fontSize: 18, color: text.tertiary }} />
                      ) : (
                        <Cancel sx={{ fontSize: 18, color: statusTokens.error }} />
                      )}
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: item.satisfied ? text.primary : text.secondary,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.optional && (
                        <Chip
                          size="small"
                          label="Optional"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            bgcolor: bg.subtle,
                            color: text.tertiary,
                          }}
                        />
                      )}
                      {item.waived && (
                        <Chip
                          size="small"
                          label="Waived"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            bgcolor: statusTokens.neutralBg,
                            color: statusTokens.neutral,
                          }}
                        />
                      )}
                      {/* A waiver only makes sense on a mandatory requirement
                          that isn't otherwise met. */}
                      {!item.optional && (!item.satisfied || item.waived) && (
                        <Button
                          size="small"
                          disabled={rowBusy === `req:${item.key}`}
                          onClick={() => toggleRequirement(item)}
                          sx={{
                            textTransform: 'none',
                            minWidth: 0,
                            px: 0.75,
                            fontSize: 12,
                            color: text.tertiary,
                          }}
                        >
                          {rowBusy === `req:${item.key}`
                            ? '…'
                            : item.waived
                              ? 'Unwaive'
                              : 'Waive'}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Section>
          ) : null}

          {/* Documents */}
          <Section
            title="Submitted documents"
            grid={false}
            action={
              data.documentsWaived ? (
                <Chip
                  size="small"
                  label="Requirements waived"
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: statusTokens.neutral,
                    bgcolor: statusTokens.neutralBg,
                  }}
                />
              ) : data.documentChecklist ? (
                <Chip
                  size="small"
                  label={`${data.documentChecklist.satisfiedCount ?? 0}/${
                    data.documentChecklist.requiredCount ?? 0
                  } approved`}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: data.documentChecklist.complete
                      ? statusTokens.successDark
                      : statusTokens.warningDark,
                    bgcolor: data.documentChecklist.complete
                      ? statusTokens.successBg
                      : statusTokens.warningBg,
                  }}
                />
              ) : null
            }
          >
            {documents.length === 0 ? (
              <Typography variant="body2" sx={{ color: text.tertiary }}>
                No documents uploaded.
              </Typography>
            ) : (
              <Box>
                {documents.map((doc, i) => (
                  <Box key={doc.id}>
                    {i > 0 && <Divider sx={{ my: 1.25 }} />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: text.primary }}>
                          {doc.documentName || titleise(doc.documentType)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: text.tertiary }}>
                          {titleise(doc.documentType)}
                          {formatSize(doc.fileSize) ? ` · ${formatSize(doc.fileSize)}` : ''} ·{' '}
                          {formatDate(doc.createdAt)}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={titleise(doc.verificationStatus)}
                        sx={{
                          textTransform: 'capitalize',
                          fontSize: 11,
                          height: 22,
                          color:
                            doc.verificationStatus === 'approved'
                              ? statusTokens.successDark
                              : doc.verificationStatus === 'rejected'
                                ? statusTokens.errorDark
                                : statusTokens.warningDark,
                          bgcolor:
                            doc.verificationStatus === 'approved'
                              ? statusTokens.successBg
                              : doc.verificationStatus === 'rejected'
                                ? statusTokens.errorBg
                                : statusTokens.warningBg,
                        }}
                      />

                      {doc.verificationStatus !== 'approved' && (
                        <Button
                          size="small"
                          disabled={rowBusy === `doc:${doc.id}`}
                          startIcon={<Done sx={{ fontSize: 15 }} />}
                          onClick={() => decideDocument(doc, 'approved')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            color: statusTokens.successDark,
                          }}
                        >
                          Approve
                        </Button>
                      )}
                      {doc.verificationStatus !== 'rejected' && (
                        <Button
                          size="small"
                          disabled={rowBusy === `doc:${doc.id}`}
                          startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                          onClick={() => decideDocument(doc, 'rejected')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            color: statusTokens.errorDark,
                          }}
                        >
                          Reject
                        </Button>
                      )}

                      {doc.fileUrl && (
                        <Link
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: 13,
                            fontWeight: 600,
                            color: brand.primary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Open <OpenInNew sx={{ fontSize: 14 }} />
                        </Link>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          {/* Realtor: agency standing on the EAC register + manual override */}
          {isRealtor && (
            <Section title="Realtor & EAC standing" grid={false}>
              <Alert
                severity={eacSeverity}
                sx={{ mb: 2 }}
                action={
                  eac?.manualOverride ? (
                    <Button
                      size="small"
                      disabled={rowBusy === 'eac'}
                      onClick={() => setEacOverride(false)}
                    >
                      Remove manual verification
                    </Button>
                  ) : eac && eac.state !== 'approved' && !eacEditing ? (
                    <Button
                      size="small"
                      disabled={rowBusy === 'eac'}
                      onClick={() => setEacEditing(true)}
                    >
                      Verify agency manually
                    </Button>
                  ) : null
                }
              >
                <strong>Agency on EAC register:</strong> {eacSummary}
                {eac?.registerLastSyncedAt && ` (register synced ${formatDate(eac.registerLastSyncedAt)})`}
              </Alert>
              {eacEditing && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="How did you confirm this agency is EAC-registered?"
                    value={eacNotes}
                    onChange={(e) => setEacNotes(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={eacNotes.trim().length < 3 || rowBusy === 'eac'}
                      onClick={() => setEacOverride(true, eacNotes.trim())}
                    >
                      Mark verified
                    </Button>
                    <Button size="small" onClick={() => { setEacEditing(false); setEacNotes(''); }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
              {/* Eqaya's own check: the agency confirms this person works there. Use the
                  agency's phone/email from the EAC register, never the realtor's. */}
              <Alert
                severity={eac?.agencyConfirmation ? 'success' : 'warning'}
                sx={{ mb: 2 }}
                action={eac?.agencyConfirmation ? (
                  <Button size="small" disabled={rowBusy === 'agc'} onClick={() => setAgencyConfirmation(false)}>Withdraw</Button>
                ) : !agcEditing ? (
                  <Button size="small" disabled={rowBusy === 'agc'} onClick={() => setAgcEditing(true)}>Record confirmation</Button>
                ) : null}
              >
                <strong>Agency confirms they work there:</strong>{' '}
                {eac?.agencyConfirmation
                  ? `confirmed ${formatDate(eac.agencyConfirmation.at)}${eac.agencyConfirmation.notes ? `: ${eac.agencyConfirmation.notes}` : ''}`
                  : 'not yet. Required before approval.'}
                {!eac?.agencyConfirmation && (
                  <Box sx={{ mt: 0.75 }}>
                    Contact the agency on the details the EAC register lists, not the realtor&apos;s own:
                    {eac?.agency ? (
                      <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                        {eac.agency.name}{eac.agency.phone ? ` · ${eac.agency.phone}` : ''}{eac.agency.email ? ` · ${eac.agency.email}` : ''}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ display: 'block' }}>No agency matched on the register yet: match it first, or verify the agency manually above.</Box>
                    )}
                    Ask them to confirm this person works for them and holds the registration number on their practising certificate.
                  </Box>
                )}
              </Alert>
              {agcEditing && (
                <Box sx={{ mb: 2 }}>
                  <TextField fullWidth multiline minRows={2} size="small" label="Who at the agency confirmed it, and how?"
                    placeholder="e.g. Called the EAC-listed office number; Mrs Dube (principal) confirmed she works here, registration no. 1234."
                    value={agcNotes} onChange={(e) => setAgcNotes(e.target.value)} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button size="small" variant="contained" disabled={agcNotes.trim().length < 3 || rowBusy === 'agc'} onClick={() => setAgencyConfirmation(true, agcNotes.trim())}>Agency confirmed</Button>
                    <Button size="small" onClick={() => { setAgcEditing(false); setAgcNotes(''); }}>Cancel</Button>
                  </Box>
                </Box>
              )}
              <Grid container spacing={2.5}>
                <Field label="Display name" value={realtorProfile?.display_name || EMPTY} />
                <Field
                  label="Agency"
                  value={realtorProfile?.agency?.name || realtorProfile?.agency_name_claimed || EMPTY}
                />
                <Field label="Agency address" value={realtorProfile?.agency?.address || EMPTY} />
                <Field label="Phone" value={realtorProfile?.phone || profile?.phone_number || EMPTY} />
                <Field
                  label="Operating cities"
                  value={
                    realtorProfile?.operating_cities?.length
                      ? realtorProfile.operating_cities.join(', ')
                      : EMPTY
                  }
                />
              </Grid>
            </Section>
          )}

          {/* Conveyancer: standing on the Law Society register + manual override */}
          {isConveyancer && (
            <Section title="Conveyancer & Law Society standing" grid={false}>
              <Alert
                severity={lszSeverity}
                sx={{ mb: 2 }}
                action={
                  lsz?.manualOverride ? (
                    <Button size="small" disabled={rowBusy === 'lsz'} onClick={() => setLszOverride(false)}>
                      Remove manual verification
                    </Button>
                  ) : lsz && lsz.state !== 'approved' && !eacEditing ? (
                    <Button size="small" disabled={rowBusy === 'lsz'} onClick={() => setEacEditing(true)}>
                      Verify manually
                    </Button>
                  ) : null
                }
              >
                <strong>Law Society register:</strong> {lszSummary}
                {lsz?.registerLastSyncedAt && ` (register synced ${formatDate(lsz.registerLastSyncedAt)})`}
              </Alert>
              {eacEditing && (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="How did you confirm they may practise as a conveyancer this year?"
                    value={eacNotes}
                    onChange={(e) => setEacNotes(e.target.value)}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={eacNotes.trim().length < 3 || rowBusy === 'lsz'}
                      onClick={() => setLszOverride(true, eacNotes.trim())}
                    >
                      Mark verified
                    </Button>
                    <Button size="small" onClick={() => { setEacEditing(false); setEacNotes(''); }}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
              <Grid container spacing={2.5}>
                <Field label="Display name" value={conveyancerProfile?.display_name || EMPTY} />
                <Field label="Law firm" value={conveyancerProfile?.firm_name || lszEntry?.firm || EMPTY} />
                <Field label="Town" value={conveyancerProfile?.town || lszEntry?.town || EMPTY} />
                <Field label="Phone" value={conveyancerProfile?.phone || profile?.phone_number || EMPTY} />
                <Field
                  label="Towns served"
                  value={conveyancerProfile?.towns_served?.length ? conveyancerProfile.towns_served.join(', ') : EMPTY}
                />
              </Grid>
            </Section>
          )}

          {/* Verifier scope */}
          {isVerifier && (
            <Section title="Verification scope">
              <Field
                label="Verifies"
                value={
                  data.verifierTypes?.length
                    ? data.verifierTypes.map((t) => VERIFIER_TYPE_LABELS[t] || t).join(', ')
                    : EMPTY
                }
              />
              <Field
                label="Service areas"
                value={profile?.service_areas?.length ? profile.service_areas.join(', ') : EMPTY}
              />
              <Field label="Available" value={profile?.is_available ? 'Yes' : 'No'} />
              <Field
                label="Max travel distance"
                value={
                  profile?.max_travel_distance != null
                    ? `${profile.max_travel_distance} km`
                    : EMPTY
                }
              />
            </Section>
          )}

          {/* Contractor trade detail */}
          {isContractor && (
            <Section title="Business & trade">
              <Field label="Account type" value={titleise(profile?.account_type)} />
              <Field label="Company" value={profile?.company_name || EMPTY} />
              <Field label="Registration no." value={profile?.registration_number || EMPTY} />
              <Field label="Primary trade" value={titleise(profile?.primary_trade)} />
              <Field
                label="Years experience"
                value={profile?.years_experience ?? EMPTY}
              />
              <Field label="Suggested tier" value={titleise(profile?.suggested_tier)} />
              <Field
                label="Specializations"
                value={
                  profile?.specializations?.length ? profile.specializations.join(', ') : EMPTY
                }
                span={12}
              />
              {profile?.certifications_text && (
                <Field label="Certifications" value={profile.certifications_text} span={12} />
              )}
            </Section>
          )}

          {/* Licensing — contractors only carry these */}
          {isContractor &&
            hasAny(
              profile?.contractor_license_number,
              profile?.contractor_license_expiry,
              profile?.insurance_policy_number,
              profile?.insurance_expiry
            ) && (
              <Section title="Licensing & insurance">
                <Field label="Licence no." value={profile?.contractor_license_number || EMPTY} />
                <Field
                  label="Licence expiry"
                  value={formatDate(profile?.contractor_license_expiry)}
                />
                <Field label="Insurance policy" value={profile?.insurance_policy_number || EMPTY} />
                <Field label="Insurance expiry" value={formatDate(profile?.insurance_expiry)} />
              </Section>
            )}

          {/* Contact */}
          <Section title="Contact">
            <Field label="Email" value={data.email} />
            <Field label="Email verified" value={data.email_verified ? 'Yes' : 'No'} />
            <Field label="Phone" value={profile?.phone_number || EMPTY} />
            <Field label="Account status" value={titleise(data.status)} />
            <Field label="City" value={profile?.city || EMPTY} />
            <Field label="Province" value={titleise(profile?.province)} />
            <Field label="Address" value={profile?.address || EMPTY} span={12} />
            {profile?.bio && <Field label="Bio" value={profile.bio} span={12} />}
          </Section>
        </>
      )}
    </Box>
  );
};

export default AdminApplicationDetailPage;
